import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, MessageCircle, Twitter, Mail } from "lucide-react";
import axios from "axios";
import { trackEvent } from "../lib/analytics";

const STORAGE_KEY = "yaba_launch_notify_v1";
const REF_FROM_KEY = "yaba_ref_from";
const POLL_INTERVAL_MS = 20000;
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PRE-LAUNCH HARD GATE + REFERRAL WAITLIST.
 *
 * - Always visible; site behind is blurred and non-interactive.
 * - On landing, captures any `?ref=CODE` from the URL, stores it, and
 *   passes it to the API so the referrer is credited on submit.
 * - After a successful submit, shows the visitor's queue position and a
 *   share block (copy URL, WhatsApp, X, Email). Every friend who signs up
 *   via their link bumps them up the leaderboard.
 * - Polls status every 20s (and on window focus) so the counter climbs
 *   live while the modal is open.
 */

function captureRefFromUrl() {
  try {
    const url = new URL(window.location.href);
    const ref = url.searchParams.get("ref");
    if (ref) {
      const clean = ref.trim().toUpperCase().slice(0, 12);
      window.localStorage.setItem(REF_FROM_KEY, clean);
      // Clean the URL — no need to keep the code in the address bar.
      url.searchParams.delete("ref");
      const qs = url.searchParams.toString();
      window.history.replaceState(
        {},
        "",
        url.pathname + (qs ? "?" + qs : "") + url.hash
      );
      return clean;
    }
    return window.localStorage.getItem(REF_FROM_KEY) || null;
  } catch {
    return null;
  }
}

function loadSaved() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.email || !parsed.referral_code) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveState(state) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

function buildShareUrl(code) {
  const origin = window.location.origin;
  return `${origin}/?ref=${code}`;
}

export function LaunchNotifyModal() {
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [copied, setCopied] = useState(false);

  const [saved, setSaved] = useState(() => loadSaved());
  const [status, setStatus] = useState(() => (loadSaved() ? "done" : "idle")); // idle | submitting | done | error
  // Capture referrer synchronously so the "A friend invited you" eyebrow
  // renders correctly on the very first paint.
  const [hasReferrer, setHasReferrer] = useState(() => !!captureRefFromUrl());

  // Keep an effect too in case something races (e.g. late-loaded query params).
  useEffect(() => {
    const ref = captureRefFromUrl();
    if (ref) setHasReferrer(true);
  }, []);

  // Body scroll is locked while the gate is up.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Live-refresh position + referral count while in success state.
  const refreshStatus = useCallback(async (code) => {
    if (!code) return;
    try {
      const { data } = await axios.get(`${API}/api/notify/status/${code}`);
      setSaved((cur) => {
        const next = { ...(cur || {}), ...data };
        saveState(next);
        return next;
      });
    } catch {
      /* ignore transient network errors */
    }
  }, []);

  useEffect(() => {
    if (status !== "done" || !saved?.referral_code) return;
    const code = saved.referral_code;
    refreshStatus(code);
    const timer = window.setInterval(() => refreshStatus(code), POLL_INTERVAL_MS);
    const onFocus = () => refreshStatus(code);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, saved?.referral_code]);

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!/.+@.+\..+/.test(clean)) {
      setStatus("error");
      setErrorMsg("Please enter a valid email");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      const referredBy = window.localStorage.getItem(REF_FROM_KEY) || null;
      const { data } = await axios.post(`${API}/api/notify/subscribe`, {
        email: clean,
        source: referredBy ? "launch_notify_referral" : "launch_notify_modal",
        referred_by: referredBy,
      });
      const next = {
        email: clean,
        referral_code: data.referral_code,
        referral_count: data.referral_count,
        position: data.position,
        total: data.total,
        ts: Date.now(),
      };
      saveState(next);
      setSaved(next);
      trackEvent("lead", {
        method: referredBy ? "launch_notify_referral" : "launch_notify",
      });
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again.");
    }
  };

  const shareUrl = saved?.referral_code ? buildShareUrl(saved.referral_code) : "";
  const shareText = "I just joined the YABA waitlist — a new Moroccan menswear label. Grab your spot before launch:";

  const copy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // Fallback for older browsers / non-secure contexts
      const el = document.createElement("textarea");
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(el);
    }
    setCopied(true);
    trackEvent("share", { method: "copy_link", content_type: "referral" });
    window.setTimeout(() => setCopied(false), 1800);
  };

  const trackShare = (method) => {
    trackEvent("share", { method, content_type: "referral" });
  };

  return (
    <motion.div
      data-testid="launch-notify-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[#0C182E]/85 backdrop-blur-md"
      />

      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby="launch-notify-title"
        className="relative w-full max-w-[560px] bg-[#0C182E] text-[#F4EDE2] border border-[#C8A96A]/25 shadow-[0_30px_80px_rgba(0,0,0,0.6)] my-auto"
        initial={{ y: 24, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-3 border border-[#C8A96A]/20"
        />

        <div className="relative px-8 md:px-12 py-10 md:py-12 text-center">
          <div
            aria-label="YABA"
            data-testid="launch-notify-logo"
            className="text-[#C8A96A] text-3xl md:text-4xl leading-none tracking-[0.02em] select-none"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            YABA<span className="align-super text-[0.4em] ml-0.5">®</span>
          </div>

          {status === "done" && saved ? (
            <SuccessBlock
              saved={saved}
              shareUrl={shareUrl}
              shareText={shareText}
              copied={copied}
              onCopy={copy}
              onShare={trackShare}
            />
          ) : (
            <IdleBlock
              email={email}
              setEmail={setEmail}
              status={status}
              errorMsg={errorMsg}
              onSubmit={submit}
              onChange={(v) => {
                setEmail(v);
                if (status === "error") {
                  setStatus("idle");
                  setErrorMsg("");
                }
              }}
              hasReferrer={hasReferrer}
            />
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ---------- Sub-components ---------- */

function IdleBlock({
  email,
  setEmail,
  onChange,
  status,
  errorMsg,
  onSubmit,
  hasReferrer,
}) {
  return (
    <>
      <p
        className="mt-6 text-[10px] tracking-[0.35em] uppercase text-[#C8A96A]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {hasReferrer ? "A friend invited you" : "Coming soon · SS26"}
      </p>
      <h2
        id="launch-notify-title"
        className="mt-4 text-3xl md:text-4xl leading-[1.1]"
        style={{ fontFamily: "Fraunces, serif" }}
      >
        Be first through the door.
      </h2>
      <p
        className="mt-4 text-sm md:text-base text-[#F4EDE2]/70 max-w-sm mx-auto"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        The store isn't quite open yet — we're finishing the last stitches.
        Leave your email and we'll ping you the moment YABA goes live, before
        anyone else.
      </p>

      <form
        onSubmit={onSubmit}
        className="mt-8 flex flex-col sm:flex-row items-stretch gap-3 max-w-md mx-auto"
      >
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => onChange(e.target.value)}
          placeholder="your@email.com"
          aria-label="Email address"
          data-testid="launch-notify-input"
          className="flex-1 bg-transparent border border-[#C8A96A]/40 focus:border-[#C8A96A] outline-none px-4 py-3 text-sm text-[#F4EDE2] placeholder:text-[#F4EDE2]/40 transition-colors"
          style={{ fontFamily: "Inter, sans-serif" }}
        />
        <button
          type="submit"
          data-testid="launch-notify-submit"
          disabled={status === "submitting"}
          className="rounded-none bg-[#C8A96A] hover:bg-[#E9CE95] disabled:opacity-60 text-[#0C182E] px-6 py-3 text-sm tracking-[0.2em] uppercase transition-colors"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {status === "submitting" ? "Adding…" : "Notify me"}
        </button>
      </form>

      {status === "error" && (
        <p
          data-testid="launch-notify-error"
          className="mt-3 text-xs text-[#B95841] tracking-[0.15em] uppercase"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {errorMsg || "Please enter a valid email"}
        </p>
      )}
    </>
  );
}

function SuccessBlock({ saved, shareUrl, shareText, copied, onCopy, onShare }) {
  const position = saved.position || 1;
  const total = saved.total || 1;
  const refs = saved.referral_count || 0;

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(
    shareText + " " + shareUrl
  )}`;
  const twitterHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    shareText
  )}&url=${encodeURIComponent(shareUrl)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent(
    "Get on the YABA waitlist"
  )}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`;

  return (
    <>
      <p
        className="mt-5 text-[10px] tracking-[0.35em] uppercase text-[#C8A96A]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        You're on the list
      </p>
      <h2
        id="launch-notify-title"
        className="mt-3 text-3xl md:text-4xl leading-[1.05]"
        style={{ fontFamily: "Fraunces, serif" }}
      >
        Shukran.
      </h2>

      {/* Position stat */}
      <div className="mt-6" data-testid="launch-notify-position">
        <div
          className="inline-flex items-baseline gap-2 text-[#C8A96A]"
          style={{ fontFamily: "Fraunces, serif" }}
        >
          <span className="text-[11px] tracking-[0.28em] uppercase text-[#F4EDE2]/50">
            You're
          </span>
          <span className="text-4xl md:text-5xl leading-none">#{position}</span>
          <span className="text-[11px] tracking-[0.28em] uppercase text-[#F4EDE2]/50">
            of {total}
          </span>
        </div>
      </div>

      <p
        className="mt-4 text-sm md:text-[15px] text-[#F4EDE2]/75 max-w-sm mx-auto leading-relaxed"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        Move up the queue — every friend who joins with your link bumps you
        higher on the leaderboard.
      </p>

      {/* Referrals earned */}
      <div
        className="mt-5 inline-flex items-center gap-3 border border-[#C8A96A]/30 px-4 py-2"
        data-testid="launch-notify-refcount"
      >
        <span
          className="text-2xl leading-none text-[#C8A96A]"
          style={{ fontFamily: "Fraunces, serif" }}
        >
          {refs}
        </span>
        <span
          className="text-[10px] tracking-[0.25em] uppercase text-[#F4EDE2]/60"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {refs === 1 ? "friend joined" : "friends joined"}
        </span>
      </div>

      {/* Share URL */}
      <div className="mt-8 max-w-md mx-auto">
        <label
          className="block text-[10px] tracking-[0.3em] uppercase text-[#F4EDE2]/50"
          style={{ fontFamily: "Inter, sans-serif" }}
          htmlFor="launch-share-url"
        >
          Your invite link
        </label>
        <div className="mt-2 flex items-stretch gap-2">
          <input
            id="launch-share-url"
            type="text"
            value={shareUrl}
            readOnly
            onFocus={(e) => e.target.select()}
            data-testid="launch-notify-share-url"
            className="flex-1 bg-transparent border border-[#C8A96A]/30 focus:border-[#C8A96A] outline-none px-3 py-2.5 text-xs text-[#F4EDE2] truncate"
            style={{ fontFamily: "Inter, sans-serif" }}
          />
          <button
            type="button"
            onClick={onCopy}
            data-testid="launch-notify-copy"
            aria-label="Copy invite link"
            className="rounded-none bg-[#C8A96A] hover:bg-[#E9CE95] text-[#0C182E] px-4 text-[11px] tracking-[0.22em] uppercase inline-flex items-center gap-2 transition-colors"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      {/* Social share buttons */}
      <div
        className="mt-5 flex items-center justify-center gap-3"
        data-testid="launch-notify-shares"
      >
        <ShareButton
          href={whatsappHref}
          label="WhatsApp"
          testId="launch-share-whatsapp"
          onClick={() => onShare("whatsapp")}
        >
          <MessageCircle size={16} strokeWidth={1.5} />
        </ShareButton>
        <ShareButton
          href={twitterHref}
          label="X"
          testId="launch-share-twitter"
          onClick={() => onShare("twitter")}
        >
          <Twitter size={16} strokeWidth={1.5} />
        </ShareButton>
        <ShareButton
          href={emailHref}
          label="Email"
          testId="launch-share-email"
          onClick={() => onShare("email")}
          openInSameTab
        >
          <Mail size={16} strokeWidth={1.5} />
        </ShareButton>
      </div>

      <div
        className="mt-8 mx-auto max-w-xs h-px bg-[#C8A96A]/25"
        aria-hidden="true"
      />
      <p
        className="mt-5 text-[10px] tracking-[0.3em] uppercase text-[#F4EDE2]/40"
        style={{ fontFamily: "Inter, sans-serif" }}
        data-testid="launch-notify-locked"
      >
        Shop opens soon · notified to {saved.email}
      </p>
    </>
  );
}

function ShareButton({ href, label, testId, onClick, openInSameTab, children }) {
  return (
    <a
      href={href}
      target={openInSameTab ? "_self" : "_blank"}
      rel="noopener noreferrer"
      data-testid={testId}
      onClick={onClick}
      className="inline-flex items-center gap-2 border border-[#C8A96A]/40 hover:border-[#C8A96A] hover:bg-[#C8A96A]/10 px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase text-[#F4EDE2] transition-colors"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {children}
      {label}
    </a>
  );
}
